"use client";

import { useEffect, useMemo } from "react";
import { getDealerAuthHeaders } from "@/app/helpers/getDealerAuthHeaders";
import finishDealerLogin from "@/helpers/finishDealerLogin";
import useBackendUrl from "@/hooks/useBackendUrl";
import { auth } from "@/config/firebaseConfig";
import { useAuth } from "@/context/AuthProvider";

const SESSION_KEY = "ael_dealer_b2b_session_v2";
const PENDING_KEY = "ael_dealer_b2b_pending_v2";
const MAX_PENDING_EVENTS = 200;
const inFlight = new Set();
const reservedIds = new Map();

export const DEALER_PROFILE_TRACKING_STEPS = Object.freeze({
  brand: "dealer_brand",
  model: "dealer_model",
  year: "dealer_year",
  bodyType: "dealer_body_type",
  fuel: "dealer_fuel",
  variant: "dealer_variant",
  power: "dealer_power",
  gearbox: "dealer_gearbox",
  doors: "dealer_doors",
  modelType: "dealer_model_type",
  mileage: "dealer_mileage",
  detailsEquipment: "dealer_equipment_history",
  detailsCondition: "dealer_condition_sale",
  detailsLocation: "dealer_location_documents",
  photoEntry: "dealer_photo_entry",
  exteriorPhotos: "dealer_exterior_photos",
  rimPhotos: "dealer_rim_photos",
  tireTreadPhotos: "dealer_tire_tread_photos",
  interiorPhotos: "dealer_interior_photos",
  bodyDamages: "dealer_body_damages",
  rimDamages: "dealer_rim_damages",
  tireDamages: "dealer_tire_damages",
  warningLights: "dealer_warning_lights",
  repainting: "dealer_repainting",
  paintThickness: "dealer_paint_thickness",
  additionalNotes: "dealer_additional_notes",
  serviceHistory: "dealer_service_history",
  serviceEvidence: "dealer_service_evidence",
  fin: "dealer_fin",
  reviewReached: "dealer_review_reached",
  pricing: "dealer_pricing",
});

export function getDealerProfileSessionId() {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid) return "";
    const key = `${SESSION_KEY}:${uid}`;
    let sessionId = window.localStorage.getItem(key);
    if (!sessionId) {
      sessionId = globalThis.crypto?.randomUUID?.() ||
        `dealer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
      window.localStorage.setItem(key, sessionId);
    }
    return sessionId;
  } catch {
    return "";
  }
}

export function finishDealerProfileSession(sessionId, uid = auth.currentUser?.uid) {
  if (!uid) return;
  reservedIds.delete(uid);
  const key = `${SESSION_KEY}:${uid}`;
  try { if (window.localStorage.getItem(key) === sessionId) window.localStorage.removeItem(key); } catch {}
}

const reservations = new Map();
export async function reserveDealerProfileSession(api) {
  const user = auth.currentUser;
  if (!user) return "";
  if (reservedIds.get(user.uid) === getDealerProfileSessionId()) return reservedIds.get(user.uid);
  if (reservations.has(user.uid)) return reservations.get(user.uid);
  const promise = (async () => {
    const localId = getDealerProfileSessionId();
    try {
      const headers = await getDealerAuthHeaders();
      const response = await fetch(`${api}/auction/dealer-vehicles/funnel/session`, { method: "POST", credentials: "include", signal: AbortSignal.timeout(5000), headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: localId, ownerEmail: user.email }) });
      if (!response.ok) return localId;
      const { sessionId } = await response.json();
      if (auth.currentUser?.uid !== user.uid) return "";
      window.localStorage.setItem(`${SESSION_KEY}:${user.uid}`, sessionId);
      reservedIds.set(user.uid, sessionId);
      writePendingEvents(readPendingEvents().map((e) => e.uid === user.uid && e.sessionId === localId && !e.vehicleId ? { ...e, sessionId } : e));
      return sessionId;
    } catch { return localId; }
  })().finally(() => reservations.delete(user.uid));
  reservations.set(user.uid, promise);
  return promise;
}

function readPendingEvents() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PENDING_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((entry) => entry?.step && entry.uid && entry.eventId).slice(-MAX_PENDING_EVENTS) : [];
  } catch {
    return [];
  }
}

function writePendingEvents(events) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(events.slice(-MAX_PENDING_EVENTS)));
  } catch {}
}

function queueEvent(event) {
  const key = event.eventId;
  const events = readPendingEvents();
  if (!events.some((entry) => entry.eventId === key)) {
    writePendingEvents([...events, event]);
  }
}

async function sendEvent(api, event) {
  if (!api || !event?.step || event.uid !== auth.currentUser?.uid) return false;
  const key = event.eventId;
  if (inFlight.has(key)) return false;
  inFlight.add(key);
  try {
    let headers = await getDealerAuthHeaders();
    if (!headers.Authorization) {
      await finishDealerLogin(api);
      headers = await getDealerAuthHeaders({ forceRefresh: true });
    }
    if (!headers.Authorization) return false;
    const payload = { sessionId: event.sessionId, vehicleId: event.vehicleId || undefined, step: event.step, type: event.type, eventId: event.eventId, ownerEmail: event.ownerEmail };
    let response = await fetch(`${api}/auction/dealer-vehicles/funnel/events`, {
      method: "POST",
      signal: AbortSignal.timeout(5000),
      credentials: "include",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (response.status === 401) {
      await finishDealerLogin(api);
      headers = await getDealerAuthHeaders({ forceRefresh: true });
      response = await fetch(`${api}/auction/dealer-vehicles/funnel/events`, {
        method: "POST",
        signal: AbortSignal.timeout(5000),
        credentials: "include",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    }
    return response.ok || [400, 403, 404, 409].includes(response.status);
  } catch {
    return false;
  } finally {
    inFlight.delete(key);
  }
}

let flushing = null;
export async function flushDealerProfileTracking(api) {
  if (flushing) return flushing;
  flushing = flushPending(api).finally(() => { flushing = null; });
  return flushing;
}
async function flushPending(api) {
  if (typeof window === "undefined") return;
  const pending = readPendingEvents();
  if (!pending.length) return;
  const processed = new Set();
  for (const event of pending) {
    // eslint-disable-next-line no-await-in-loop
    if (event.uid !== auth.currentUser?.uid || (event.nextAttemptAt || 0) > Date.now()) continue;
    if (await sendEvent(api, event)) processed.add(event.eventId);
    else { event.attempts = Math.min(5, event.attempts + 1); event.nextAttemptAt = Date.now() + Math.min(600000, 15000 * 2 ** event.attempts); }
  }
  const attempted = new Map(pending.map((e) => [e.eventId, e]));
  writePendingEvents(readPendingEvents().filter((e) => !processed.has(e.eventId)).map((e) => attempted.get(e.eventId) || e));
}

export async function trackDealerProfileReached(api, { sessionId, vehicleId, step, type = "reached" }) {
  if (!api || !step) return;
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const ownerEmail = auth.currentUser.email;
  const resolvedId = vehicleId ? sessionId || getDealerProfileSessionId() : await reserveDealerProfileSession(api);
  if (auth.currentUser?.uid !== uid || !resolvedId) return;
  const event = { uid, ownerEmail, sessionId: resolvedId, vehicleId: vehicleId || "", step, type, eventId: crypto.randomUUID(), attempts: 0 };
  queueEvent(event);
  void flushDealerProfileTracking(api);
}

// A single shared hook keeps page tracking non-blocking and guarantees that
// all dealer pages use the same authenticated retry path.
export function useDealerProfileTracking({ vehicleId = "", step = "", preVehicle = false } = {}) {
  const api = useBackendUrl();
  const { user } = useAuth();
  const sessionId = useMemo(
    () => (preVehicle && user?.uid ? getDealerProfileSessionId() : ""),
    [preVehicle, user?.uid],
  );

  useEffect(() => {
    void flushDealerProfileTracking(api);
    const flush = () => void flushDealerProfileTracking(api);
    window.addEventListener("online", flush);
    window.addEventListener("focus", flush);
    const timer = window.setInterval(flush, 15000);
    return () => { window.removeEventListener("online", flush); window.removeEventListener("focus", flush); window.clearInterval(timer); };
  }, [api, user?.uid]);

  useEffect(() => {
    if (!step || (!vehicleId && !sessionId)) return;
    trackDealerProfileReached(api, { sessionId, vehicleId, step });
  }, [api, sessionId, step, vehicleId]);

  return { api, sessionId, trackReached: (nextStep) => trackDealerProfileReached(api, { sessionId, vehicleId, step: nextStep }), trackCompleted: (nextStep) => trackDealerProfileReached(api, { sessionId, vehicleId, step: nextStep, type: "completed" }) };
}
